import { extractPythonFormatTemplateVariables } from '@apps/web/lib/python_format_template';

/**
 * Variables that put HIPAA identifiers into an unencrypted SMS.
 *
 * Names and any geographic subdivision below state level are Safe Harbor
 * identifiers, so city, street name, and zip code all belong here even though
 * each is only a partial address on its own. `location_text` and
 * `previous_visits` are free-text/treatment-history variables that render
 * location and care history the same way, and `patient_text` reads as a
 * generic phrase but renders as " for <client first name>". Both canonical and
 * alias spellings are listed because a template may reference either.
 */
const CLIENT_IDENTIFYING_TEMPLATE_VARIABLES = new Set([
    'client_address',
    'client_city',
    'client_first_name',
    'client_first_initial',
    'client_initials',
    'client_last_name',
    'client_last_initial',
    'client_name',
    'client_street_name',
    'client_zip_code',
    'location_text',
    'patient_address',
    'patient_city',
    'patient_first_name',
    'patient_last_name',
    'patient_name',
    'patient_street_name',
    'patient_text',
    'patient_zip_code',
    'previous_visits',
]);

/**
 * Return the client-identifying variables referenced by one SMS template.
 * Invalid templates are ignored here and remain the editor validator's concern.
 */
export function getClientIdentifyingTemplateVariables(
    template: string,
): ReadonlyArray<string> {
    try {
        return Array.from(
            extractPythonFormatTemplateVariables(template),
        ).filter((variable) =>
            CLIENT_IDENTIFYING_TEMPLATE_VARIABLES.has(variable),
        );
    } catch {
        return [];
    }
}

/**
 * Return the client-identifying variables referenced by a set of SMS
 * templates, deduplicated and in the order they are first referenced so the
 * list can be shown back to the author.
 */
export function collectClientIdentifyingTemplateVariables(
    templates: ReadonlyArray<string>,
): ReadonlyArray<string> {
    return Array.from(
        new Set(
            templates.flatMap((template) =>
                getClientIdentifyingTemplateVariables(template),
            ),
        ),
    );
}
